import React, { Component, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Chip,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getAppInsights } from '../../Services/Global';
import logger from '../../Services/LoggingService';

interface ComponentErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  componentName?: string;
}

interface ComponentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ComponentErrorInfo | null;
  errorId: string;
}

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  componentName: string;
  fallback?: ReactNode;
  minimal?: boolean;
  onError?: (error: Error, errorInfo: ComponentErrorInfo) => void;
}

// Functional component for component error display
const ComponentErrorDisplay: React.FC<{
  error: Error;
  errorInfo: ComponentErrorInfo;
  errorId: string;
  componentName: string;
  minimal: boolean;
  onRetry: () => void;
}> = ({ error, errorInfo, errorId, componentName, minimal, onRetry }) => {
  const theme = useTheme();

  if (minimal) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing(2),
          backgroundColor: theme.palette.error.light,
          borderRadius: theme.shape.borderRadius,
          minHeight: 60
        }}
      >
        <WarningAmberIcon sx={{ mr: 1, color: theme.palette.error.dark }} />
        <Typography variant="body2" color="error.dark">
          {componentName} failed to load
        </Typography>
        <Button
          size="small"
          onClick={onRetry}
          sx={{ ml: 2 }}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const styles = {
    container: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.warning.light}`,
      borderRadius: theme.shape.borderRadius,
      textAlign: 'center' as const
    },
    errorIcon: {
      fontSize: 40,
      color: theme.palette.warning.main,
      marginBottom: theme.spacing(1)
    },
    actionButton: {
      marginTop: theme.spacing(2)
    },
    errorChip: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  };

  return (
    <Box sx={styles.container}>
      <WarningAmberIcon sx={styles.errorIcon} />
      
      <Typography variant="h6" color="warning.main" gutterBottom>
        Component Error
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        The {componentName} component encountered an error and couldn't render properly.
      </Typography>

      <Chip 
        label={`Error ID: ${errorId}`}
        variant="outlined"
        size="small"
        sx={styles.errorChip}
      />

      <Box sx={styles.actionButton}>
        <Button
          variant="contained"
          color="primary"
          onClick={onRetry}
          startIcon={<RefreshIcon />}
          size="small"
        >
          Retry Component
        </Button>
      </Box>

      {process.env.NODE_ENV === 'development' && (
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
          <AlertTitle>Development Error Details</AlertTitle>
          <Typography variant="body2" component="div">
            <strong>Component:</strong> {componentName}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Error:</strong> {error.name}: {error.message}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

class ComponentErrorBoundary extends Component<ComponentErrorBoundaryProps, ComponentErrorBoundaryState> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ComponentErrorBoundaryState> {
    const errorId = `CMP-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId;
    const componentName = this.props.componentName;
    
    logger.error(`Component Error Boundary (${componentName}) caught an error`, error, {
      errorId: errorId,
      componentName: componentName,
      componentStack: errorInfo.componentStack
    }, {
      component: 'ComponentErrorBoundary',
      method: 'componentDidCatch'
    });

    const errorDetails: ComponentErrorInfo = {
      componentStack: errorInfo.componentStack || '',
      errorBoundary: 'ComponentErrorBoundary',
      componentName: componentName
    };

    // Call onError callback if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorDetails);
      } catch (callbackError) {
        logger.error('Error in ComponentErrorBoundary onError callback', callbackError as Error, {
          componentName: componentName,
          errorId: errorId
        }, {
          component: 'ComponentErrorBoundary',
          method: 'componentDidCatch'
        });
      }
    }

    // Log to Application Insights
    try {
      const appInsights = getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error,
          properties: {
            errorBoundary: 'ComponentErrorBoundary',
            componentName: componentName,
            errorId: errorId,
            componentStack: errorInfo.componentStack || '',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href || 'unknown',
            userId: 'unknown',
          }
        });
        
        appInsights.trackEvent({
          name: 'ErrorBoundary:ComponentError',
          properties: {
            errorId: errorId,
            errorName: error.name,
            errorMessage: error.message,
            boundary: 'Component',
            componentName: componentName,
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (loggingError) {
      logger.error('Failed to log component error to Application Insights', loggingError as Error, {
        componentName: componentName,
        errorId: errorId
      }, {
        component: 'ComponentErrorBoundary',
        method: 'componentDidCatch'
      });
    }

    this.setState({
      errorInfo: errorDetails
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ComponentErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          componentName={this.props.componentName}
          minimal={this.props.minimal || false}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary; 